import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';

// ─── Smoke Puff data ───────────────────────────────────────────────────────
class SmokePuff {
  constructor(canvasW, canvasH) {
    this.reset(canvasW, canvasH, true);
  }

  reset(W, H, randomY = false) {
    this.x     = W * 0.1 + Math.random() * W * 0.8;          // spread wider
    this.y     = randomY ? Math.random() * H : H + 120;       // start below viewport
    this.radius     = 120 + Math.random() * 220;              // bigger, cloudier
    this.targetRadius = this.radius * (1.4 + Math.random()); // grow as it rises
    this.opacity    = 0;
    this.targetOpacity = 0.025 + Math.random() * 0.045;      // very subtle
    this.speed      = 0.10 + Math.random() * 0.22;           // slower rise
    this.drift      = (Math.random() - 0.5) * 0.25;          // gentle sway
    this.rotation   = Math.random() * Math.PI * 2;
    this.rotSpeed   = (Math.random() - 0.5) * 0.002;
    // Color palette: dark muted tones only
    const palette = [
      { r: 80,  g: 70,  b: 180 },  // muted indigo
      { r: 20,  g: 130, b: 180 },  // muted cyan
      { r: 40,  g: 20,  b: 90  },  // deep violet
      { r: 8,   g: 8,   b: 28  },  // near-black
      { r: 30,  g: 30,  b: 80  },  // dark blue
    ];
    const c = palette[Math.floor(Math.random() * palette.length)];
    this.r = c.r; this.g = c.g; this.b = c.b;
    this.W = W; this.H = H;
    this.born = false;
  }

  update(mouseX, mouseY) {
    // Rise & drift
    this.y     -= this.speed;
    this.x     += this.drift + mouseX * 0.012;
    this.rotation += this.rotSpeed;
    // Grow radius
    if (this.radius < this.targetRadius) this.radius += 0.25;
    // Fade in then fade out
    const progress = 1 - this.y / this.H;          // 0 at bottom, 1 at top
    if (progress < 0.15)      this.opacity = this.targetOpacity * (progress / 0.15);
    else if (progress < 0.7)  this.opacity = this.targetOpacity;
    else                       this.opacity = this.targetOpacity * ((1 - progress) / 0.3);
    // Recycle when off-screen
    if (this.y < -this.radius * 2) this.reset(this.W, this.H);
  }

  draw(ctx) {
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.rotate(this.rotation);

    const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, this.radius);
    grad.addColorStop(0,   `rgba(${this.r},${this.g},${this.b},${this.opacity})`);
    grad.addColorStop(0.4, `rgba(${this.r},${this.g},${this.b},${this.opacity * 0.5})`);
    grad.addColorStop(1,   `rgba(${this.r},${this.g},${this.b},0)`);

    ctx.fillStyle = grad;
    ctx.beginPath();
    // Slightly elliptical blob
    ctx.ellipse(0, 0, this.radius, this.radius * 0.75, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

// ─── Component ────────────────────────────────────────────────────────────
export default function ThreeScene() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let W = window.innerWidth;
    let H = window.innerHeight;
    canvas.width  = W;
    canvas.height = H;

    // Mouse tracking (normalized -1 → 1)
    let mouseX = 0, mouseY = 0;
    let smoothX = 0, smoothY = 0;
    const handleMouse = (e) => {
      mouseX = (e.clientX / W - 0.5) * 2;
      mouseY = (e.clientY / H - 0.5) * 2;
    };
    window.addEventListener('mousemove', handleMouse);

    // ─── Create smoke puffs ───
    const PUFF_COUNT = 20;
    const puffs = Array.from({ length: PUFF_COUNT }, () => new SmokePuff(W, H));

    // ─── GSAP entrance: fade canvas in ───
    gsap.fromTo(canvas, { opacity: 0 }, { opacity: 1, duration: 2.5, ease: 'power2.inOut' });

    // ─── Stagger puffs so they don't all rise at once ───
    puffs.forEach((p, i) => {
      gsap.delayedCall(i * 0.3, () => { p.born = true; });
    });

    // ─── Animation loop ───
    let raf;
    const tick = () => {
      raf = requestAnimationFrame(tick);

      // Smooth mouse
      smoothX += (mouseX - smoothX) * 0.04;
      smoothY += (mouseY - smoothY) * 0.04;

      // Clear
      ctx.clearRect(0, 0, W, H);

      // Draw puffs
      puffs.forEach((p) => {
        if (!p.born) return;
        p.update(smoothX, smoothY);
        p.draw(ctx);
      });
    };
    tick();

    // ─── Resize ───
    const handleResize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width  = W;
      canvas.height = H;
      puffs.forEach((p) => { p.W = W; p.H = H; });
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', handleMouse);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
        opacity: 0,           // GSAP will fade in
      }}
    />
  );
}
